<script setup lang="ts">
import { computed } from 'vue';
import type { AudioFileIssueSeverity } from '@/composables/api/useAudioFileIssuesApi';
import { AUDIO_FILE_ISSUE_SEVERITY_LABELS } from '@/constants/audioFileIssueEnums';

/**
 * Single source of truth for the audio-file-issue severity pill. Used by
 * the list cell, the inspector header, and the filter builder dropdown so
 * "error" / "warning" / "info" always read with the same colour and casing.
 *
 * The shape stays minimal on purpose — pure presentation, no row-level
 * click handling — so the component can be embedded anywhere without
 * pulling in the inspector or list-table context.
 */
const props = withDefaults(
  defineProps<{
    /**
     * Severity slug (`error` | `warning` | `info`). Aliased to `value` so the
     * shared filter-builder dropdown wrappers
     * ({@link FilterEnumSelect}, {@link FilterMultiCombobox}) can pass options
     * through with their generic `{ value, label }` shape.
     */
    severity?: AudioFileIssueSeverity;
    value?: AudioFileIssueSeverity;
    label?: string | null;
    /** Unused. Declared so FilterMultiCombobox's `:option` stays out of the DOM. */
    option?: unknown;
    /**
     * Slight size variant for places that need a denser badge (e.g. inside
     * a dropdown option) without changing the colour mapping.
     */
    size?: 'sm' | 'md';
  }>(),
  { size: 'md', label: null, severity: undefined, value: undefined },
);

const resolvedSeverity = computed<AudioFileIssueSeverity>(
  () => props.severity ?? props.value ?? 'info',
);

const severityClass = computed(() => {
  if (resolvedSeverity.value === 'error') {
    return 'bg-destructive/15 text-destructive';
  }
  if (resolvedSeverity.value === 'warning') {
    return 'bg-amber-500/15 text-amber-700 dark:text-amber-400';
  }
  return 'bg-muted text-muted-foreground';
});

const sizeClass = computed(() =>
  props.size === 'sm'
    ? 'px-1.5 py-0.5 text-[10px]'
    : 'px-1.5 py-0.5 text-[11px] font-medium',
);

const displayLabel = computed(
  () =>
    props.label ??
    AUDIO_FILE_ISSUE_SEVERITY_LABELS[resolvedSeverity.value] ??
    resolvedSeverity.value,
);
</script>

<template>
  <span
    class="rounded uppercase tracking-wide"
    :class="[severityClass, sizeClass]"
    data-testid="audio-file-issue-severity-badge"
  >
    {{ displayLabel }}
  </span>
</template>
